import {
    Injectable,
    Inject,
    PLATFORM_ID
} from '@angular/core';

import {
    HttpClient,
    HttpBackend
} from '@angular/common/http';

import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_URLS } from '../config/api.urls';


@Injectable()
export class AuthenticationService {

    /**
     * rawHttp is deliberately created from HttpBackend.
     *
     * Why?
     *
     * The normal HttpClient goes through all Angular interceptors.
     * The refresh request must NOT go through ErrorInterceptor again,
     * otherwise a failed refresh could trigger another refresh and create
     * a recursive authentication loop.
     */
    private readonly rawHttp: HttpClient;


    constructor(
        private http: HttpClient,
        private httpBackend: HttpBackend,
        @Inject(PLATFORM_ID)
        private platformId: Object
    ) {
        this.rawHttp = new HttpClient(this.httpBackend);
    }


    // =========================================================================
    // LOGIN
    // =========================================================================

    login(params: any): Observable<any> {
        return this.http.post(
            API_URLS.LOGIN,
            params
        );
    }


    // =========================================================================
    // LOGOUT
    // =========================================================================

    logout(): void {

        /**
         * Logout API call is best-effort.
         *
         * We do not wait for it before clearing local authentication data.
         */
        try {
            this.http.delete(
                API_URLS.LOGOUT
            ).subscribe({
                error: () => {
                    // Ignore logout API failure.
                    // Local credentials are still cleared below.
                }
            });
        } catch (e) {
            // Ignore logout API errors.
        }


        if (isPlatformBrowser(this.platformId)) {

            localStorage.removeItem('currentUser');
            localStorage.removeItem('tokenDetails');
            localStorage.removeItem('tokenExpiredAt');
        }
    }


    // =========================================================================
    // TOKEN STORAGE
    // =========================================================================

    /**
     * Get token details safely from localStorage.
     */
    getTokenDetails(): any | null {

        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const tokenDetailsJson =
            localStorage.getItem('tokenDetails');

        if (!tokenDetailsJson) {
            return null;
        }

        try {
            return JSON.parse(tokenDetailsJson);
        } catch (error) {

            console.error(
                'Invalid tokenDetails stored in localStorage',
                error
            );

            // Corrupted authentication data should not remain.
            localStorage.removeItem('tokenDetails');
            localStorage.removeItem('tokenExpiredAt');

            return null;
        }
    }


    /**
     * Save the complete token response.
     *
     * This supports refresh-token rotation because the backend may return
     * a NEW refresh_token together with the new access_token.
     */
    saveTokenDetails(tokenDetails: any): boolean {

        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }

        if (
            !tokenDetails ||
            !tokenDetails.access_token
        ) {
            console.error(
                'Cannot save authentication tokens: access_token is missing'
            );

            return false;
        }

        try {

            localStorage.setItem(
                'tokenDetails',
                JSON.stringify(tokenDetails)
            );


            /**
             * Backend returns expired_time in seconds.
             *
             * Example:
             * expired_time = 900
             *
             * We convert it to milliseconds and store the exact
             * expiration timestamp.
             */
            const expiredTime =
                Number(tokenDetails.expired_time);


            if (
                Number.isFinite(expiredTime) &&
                expiredTime > 0
            ) {

                const tokenExpiredAt =
                    Date.now() +
                    (expiredTime * 1000);


                localStorage.setItem(
                    'tokenExpiredAt',
                    tokenExpiredAt.toString()
                );

            } else {

                // Do not leave an old expiry timestamp behind.
                localStorage.removeItem(
                    'tokenExpiredAt'
                );
            }


            return true;

        } catch (error) {

            console.error(
                'Failed to save authentication tokens',
                error
            );

            return false;
        }
    }


    /**
     * Clear only authentication token storage.
     */
    clearTokenStorage(): void {

        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        localStorage.removeItem('tokenDetails');
        localStorage.removeItem('tokenExpiredAt');
    }


    // =========================================================================
    // CHECK TOKEN
    // =========================================================================

    checkToken(): Observable<any> | null {

        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const tokenDetails =
            this.getTokenDetails();

        const accessToken =
            tokenDetails?.access_token ?? '';


        return this.http.get(
            API_URLS.CHECK_TOKEN,
            {
                params: {
                    token: accessToken
                }
            }
        );
    }


    // =========================================================================
    // REFRESH TOKEN
    // =========================================================================

    /**
     * Refresh the access token.
     *
     * IMPORTANT:
     *
     * We deliberately use rawHttp instead of this.http.
     *
     * rawHttp bypasses Angular interceptors, so the refresh endpoint
     * cannot accidentally trigger ErrorInterceptor recursively.
     */
refreshToken(refreshToken: string): Observable<any> {
        return this.rawHttp.post(
            API_URLS.REFRESH_TOKEN,
            {
                refreshToken
            }
        ).pipe(
            map((response: any) => response.data)
        );
    }


    // =========================================================================
    // TOKEN EXPIRATION
    // =========================================================================

    /**
     * Returns TRUE when the access token is expired.
     *
     * The old method was named isTokenValid(), but its implementation
     * actually returned TRUE when the token had expired.
     *
     * This method uses the correct name and behavior.
     */
    isTokenExpired(): boolean {

        if (!isPlatformBrowser(this.platformId)) {
            return true;
        }

        const tokenExpiredAt =
            localStorage.getItem('tokenExpiredAt');


        if (!tokenExpiredAt) {

            /**
             * If we don't know when the token expires, let the
             * server/interceptor determine the actual state.
             */
            return false;
        }


        const expiration =
            Number(tokenExpiredAt);


        if (
            !Number.isFinite(expiration)
        ) {
            return true;
        }


        return Date.now() >= expiration;
    }


    isTokenValid(): boolean {

        return !this.isTokenExpired();
    }
}